import {
  createSlice,
  createAsyncThunk,
  createSelector } from
"@reduxjs/toolkit";
import { ApolloClient, gql, NormalizedCacheObject } from "@apollo/client";
import { RootState } from "@src/redux/store";
import * as TYPES from "@src/redux/types";

const GRAPHQL = gql`
  query login($data: loginInput!) {
    login(data: $data) {
      status
      userId
      sessionId
      hasNCD
      pageAccesses
      pageComponentAccesses
      systemUser {
        roleList {
          id
          name
          homeUiModule {
            id
            name
          }
        }
        mobile
        name
        id
      }
      error {
        statusCode
        message
      }
      forcePasswordChange
      homePage
    }
  }
`;

const initialState: TYPES.LoginState = {
  result: null,
  error: null,
  loading: false
};

export const loginRequest = createAsyncThunk<
  TYPES.Login,
  {data: TYPES.LoginInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  "system_user/login",
  async (params: {data: TYPES.LoginInput;}, { extra: client }) => {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.login;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || "An error occurred",
        statusCode: err.statusCode || "500"
      });
    }
  }
);

const loginSlice = createSlice({
  name: "login",
  initialState: initialState,
  reducers: {
    loginClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(loginRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(loginRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(loginRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: "Failed to fetch",
        statusCode: "500"
      };
      state.loading = false;
    });
  }
});

export const { loginClear } = loginSlice.actions;

export const login = createSelector(
  (state: RootState) => state.systemUser.login.result,
  (state: RootState) => state.systemUser.login.error,
  (state: RootState) => state.systemUser.login.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default loginSlice.reducer;